// DEM-08 (multi-provider v5): seleção de provider LLM via dropdown.
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Select } from '@/components/ui/Select';

interface ProviderSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Filtra providers que suportam o auth_method dado. */
  filterByAuthMethod?: 'oauth' | 'api_key' | 'adc';
}

export function ProviderSelect({
  value,
  onChange,
  label = 'Provider',
  placeholder = 'Escolha o provider…',
  disabled,
  filterByAuthMethod,
}: ProviderSelectProps) {
  const { data: providers = [], isLoading } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => api.listLlmProviders(),
    staleTime: 5 * 60 * 1000,
  });

  const filtered = filterByAuthMethod
    ? providers.filter((p) => p.supported_auth_methods.includes(filterByAuthMethod))
    : providers;

  // Ordem fixa pra display: OAuth sempre antes de API Key (mais comum/seguro
  // primeiro). Antes a description vinha na ordem do array vindo do DB —
  // anthropic seedado ['oauth','api_key'] → "OAuth / API Key";
  // openai seedado ['api_key','oauth'] → "API Key / OAuth". Inconsistente.
  const AUTH_DISPLAY_ORDER: Record<string, number> = { oauth: 1, api_key: 2, adc: 3 };
  const AUTH_LABELS: Record<string, string> = { oauth: 'OAuth', api_key: 'API Key', adc: 'ADC' };
  const options = filtered.map((p) => {
    const sorted = [...p.supported_auth_methods].sort(
      (a, b) => (AUTH_DISPLAY_ORDER[a] ?? 99) - (AUTH_DISPLAY_ORDER[b] ?? 99),
    );
    return {
      value: p.key,
      label: p.display_name,
      description: sorted.map((m) => AUTH_LABELS[m] ?? m).join(' / '),
    };
  });

  return (
    <Select
      label={label}
      placeholder={isLoading ? 'Carregando providers…' : placeholder}
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled || isLoading}
    />
  );
}
